import React, { useEffect, useState } from 'react';
import {
  ContactListOutlined,
  EditableOutlined,
  SettingsOutlined,
} from '@duxton-web/icons';
import {
  Button,
  Divider,
  Form,
  notification,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';

import { useAppNavigate } from '../../../../utils/navigation';
import {
  useTreatments,
  useVariables,
} from '../../contexts/ExperimentFormInfoProvider';
import {
  ExperimentAssignmentStrategy,
  ExpFormField,
  SELECTED_EXPERIMENT_LS_KEY,
} from '../../helper/consts';
import { convertExperimentFormDataToExperimentPayload } from '../../helper/experiment';
import EditExperimentModal, {
  getAssignByFacetFromDomain,
} from '../../Modals/EditExperimentModal';
import ExperimentReviewModal from '../../Modals/ExperimentReviewModal';
import { ABIcon } from '@/common/Icons/ABIcon';
import { FeedbackType, openInAppFeedback } from '@/common/inAppFeedback';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import Service, {
  Experiment,
  ExperimentAnalysis,
  ExperimentReview,
} from '@/lib/App';
import { error } from '@/utils/logging';
import { getSearchParamsObject } from '@/utils/url';
import { getLoggedUserEmail } from '@/utils/user';

const { Text } = Typography;

const Header = () => {
  const { navigateTo } = useAppNavigate();
  const location = useLocation();
  const form = Form.useFormInstance();

  const experimentName = Form.useWatch(ExpFormField.NAME, {
    form,
    preserve: true,
  });

  const parsedParams = getSearchParamsObject(location.search);

  const [selectedExperiment] = useLocalStorage<Experiment>(
    SELECTED_EXPERIMENT_LS_KEY,
  );

  const userEmail = getLoggedUserEmail();

  const [variables] = useVariables();
  const [treatments] = useTreatments();

  const isUninitializedForm = Form.useWatch([ExpFormField.NAME], {
    form,
    preserve: true,
  })
    ? false
    : true;

  const selectedDomainName = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });

  const assignByFacet = getAssignByFacetFromDomain(selectedDomainName);

  const selectedStrategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });

  const owner = Form.useWatch(ExpFormField.OWNER, {
    form,
    preserve: true,
  });

  const [showEditExperimentModal, setShowEditExperimentModal] = useState(false);

  const [isValidating, setIsValidating] = useState(false);
  const [reviewResult, setReviewResult] = useState<ExperimentReview>(null);

  const selectedExperimentMatchWithParams =
    !!selectedExperiment &&
    !!parsedParams.expName &&
    selectedExperiment.name === parsedParams.expName;

  const isCloneMode =
    selectedExperimentMatchWithParams && parsedParams.action === 'clone';

  const isEditMode =
    selectedExperimentMatchWithParams && parsedParams.action === 'edit';

  const [initializedCloneExperiment, setInitializedCloneExperiment] =
    useState(false);

  const serverSideReview = (
    experiment: Experiment,
    analysis_v2?: ExperimentAnalysis,
  ) => {
    return Service.planner.reviewExperiment(experiment, analysis_v2);
  };

  useEffect(() => {
    const isDomainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
    const isDomainOverlapSupported =
      Service.cache.isDomainOverlapSupported(selectedDomainName);
    const isTimeSliceStrategy = [
      ExperimentAssignmentStrategy.TIME_SLICE,
      ExperimentAssignmentStrategy.TIME_SLICE_V2,
      ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
      ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
      ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
      ExperimentAssignmentStrategy.EXPLICIT_RANGE,
    ].includes(selectedStrategyName);
    if (
      (isDomainOverlapSupported || isDomainlessFeatureFlag) &&
      !isTimeSliceStrategy
    ) {
      form.setFieldValue(ExpFormField.OVERLAP_BY_DEFAULT, true);
    } else {
      form.setFieldValue(ExpFormField.OVERLAP_BY_DEFAULT, false);
    }
  }, [selectedDomainName, selectedStrategyName]);

  useEffect(() => {
    if (!experimentName) {
      setShowEditExperimentModal(true);
    }
  }, [experimentName]);

  const validateForm = async () => {
    const experimentFormData = form.getFieldsValue(true);

    // Validate experiment data
    try {
      await form.validateFields();
    } catch (e) {
      form.scrollToField(e.errorFields[0].name, {
        behavior: 'smooth',
      });
      return;
    }

    // Validate analysis config data
    const analysisV2 = experimentFormData.analysis_v2;
    analysisV2?.setShowValidation(true);

    // -- Block validation if there are metric validation errors
    if (analysisV2?.hasValidationErrors) {
      notification.error({
        message: 'Validate an Experiment',
        description: 'Invalid analysis config, please check again.',
      });
      const metricsWrapper = document.querySelector('.metrics-wrapper');
      if (metricsWrapper) {
        metricsWrapper.scrollIntoView({ behavior: 'smooth' });
      } else {
        error(
          'Unexpected error: invalid analysis config while metrics wrapper not found',
          JSON.stringify({ analysisV2 }),
        );
      }
      return;
    }

    let payload = convertExperimentFormDataToExperimentPayload({
      experimentFormData,
      variables,
      treatments,
    });

    setIsValidating(true);
    let reviewResponse: ExperimentReview;
    try {
      // Custom payload for server-side review
      const reviewPayload = { ...payload };
      reviewPayload.experiment.name = `${experimentFormData.domain}.${experimentFormData.name}`;

      reviewResponse = await serverSideReview(
        reviewPayload.experiment,
        reviewPayload.analysis_v2,
      );
      setReviewResult(reviewResponse);

      scribe.sendEvent(newCreationFlowEvents.clickValidate.name, {
        name: experimentFormData.name,
        errors: reviewResponse.errors,
        json_view: JSON.stringify(payload),
        STATE_NAME: newCreationFlowEvents.clickValidate.state,
      });
    } catch (e) {
      notification.error({
        message: 'Validate an Experiment',
        description: 'Failed to validate the experiment',
      });
      return;
    } finally {
      setIsValidating(false);
    }
  };

  const submitForm = async () => {
    try {
      const experimentFormData = form.getFieldsValue(true);
      const experimentPayload = convertExperimentFormDataToExperimentPayload({
        experimentFormData,
        variables,
        treatments,
      });

      if (isEditMode) {
        // Edit exp
        try {
          await Service.planner.editExperiment(experimentPayload);
          notification.success({
            message: 'Edit an Experiment',
            description: 'Experiment was successfully edited.',
          });

          navigateTo(`/t/experiments`);
        } catch (e) {
          console.error('e: ', e);
          const errorData = e?.response?.data;
          notification.error({
            message: 'Editing an Experiment',
            description: (
              <>
                <div>Experiment failed to edit</div>
                {errorData?.arg && <div>Error: {errorData.arg}</div>}
              </>
            ),
          });
          throw e;
        }
      } else {
        try {
          await Service.planner.createExperiment(experimentPayload);
          notification.success({
            message: 'Creating an Experiment',
            description: 'Experiment was successfully created.',
          });

          navigateTo(`/t/experiments`);

          scribe.sendEvent(newCreationFlowEvents.submitReviewModal.name, {
            name: experimentPayload.experiment.name,
            errors: reviewResult.errors,
            warnings: reviewResult.warnings,
            json_view: JSON.stringify(experimentPayload),
            STATE_NAME: newCreationFlowEvents.submitReviewModal.state,
            submitStatus: 'success',
          });

          openInAppFeedback({
            type: FeedbackType.CSAT,
            context: 'expCreationFlow',
            event: 'expCreated',
          });
        } catch (e) {
          console.error('e: ', e);
          const errorData = e?.response?.data;
          scribe.sendEvent(newCreationFlowEvents.submitReviewModal.name, {
            name: experimentPayload.experiment.name,
            errors: errorData ? JSON.stringify(errorData) : null,
            json_view: JSON.stringify(experimentPayload),
            STATE_NAME: newCreationFlowEvents.submitReviewModal.state,
            submitStatus: 'failed',
          });
          notification.error({
            message: 'Creating an Experiment',
            description: (
              <>
                <div>Experiment failed to create</div>
                {errorData?.arg && <div>Error: {errorData.arg}</div>}
              </>
            ),
          });
          throw e;
        }
      }
    } catch (e) {
      return;
    }
  };

  const handleClickEditExperiment = () => {
    const experimentFormData = form.getFieldsValue(true);
    let payload = convertExperimentFormDataToExperimentPayload({
      experimentFormData,
      variables,
      treatments,
    });
    scribe.sendEvent(newCreationFlowEvents.clickEditExperiment.name, {
      json_dump: JSON.stringify(payload),
      STATE_NAME: newCreationFlowEvents.clickEditExperiment.state,
    });
    setShowEditExperimentModal(true);
  };

  return (
    <>
      <Wrapper>
        <div style={{ alignSelf: 'flex-start' }}>
          <ABIcon />
        </div>
        <Overview>
          <FirstRowWrapper>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ExperimentName>
                <Typography.Text strong style={{ color: '#161a1d' }}>
                  Experiment
                </Typography.Text>
                {' / '}
                {form.getFieldValue(ExpFormField.NAME)}
              </ExperimentName>
              <div
                style={{ lineHeight: 0, cursor: 'pointer' }}
                onClick={() => {
                  handleClickEditExperiment();
                }}
              >
                <EditableOutlined />
              </div>
            </div>
            <ActionWrapper>
              <Button
                icon={<SettingsOutlined />}
                onClick={() => {
                  handleClickEditExperiment();
                }}
                size="small"
                type="secondary"
                shape="circle"
              />
              <Button
                icon={<ContactListOutlined />}
                onClick={() => {
                  window.open(
                    'https://techdocs.grab.com/grabx/grabx-user-guide/experiments/experiments_how-to-create-an-experiment/',
                    '_blank',
                  );
                }}
                size="small"
                type="secondary"
                shape="circle"
              />
              <Divider type="vertical" style={{ margin: 0 }} />
              <Button
                onClick={(_e) => {
                  validateForm();
                }}
                type="secondary"
                size="small"
                disabled={isValidating}
              >
                Validate Experiment
              </Button>
            </ActionWrapper>
          </FirstRowWrapper>
          <DescriptionWrapper>
            <DetailTitle strong>Hypothesis:</DetailTitle>{' '}
            <Tooltip
              title={form.getFieldValue(ExpFormField.DESCRIPTION)}
              overlayStyle={{ maxWidth: 600 }}
            >
              <DetailContent type="secondary">
                {form.getFieldValue(ExpFormField.DESCRIPTION)}
              </DetailContent>
            </Tooltip>
          </DescriptionWrapper>
          <DetailWrapper>
            <div>
              <DetailTitle strong>Creator:</DetailTitle>{' '}
              <DetailContent type="secondary">
                {owner || userEmail}
              </DetailContent>
            </div>
            <Divider type="vertical" style={{ margin: 0 }} />
            <div>
              <DetailTitle strong>Type:</DetailTitle>{' '}
              <DetailContent type="secondary">A/B test</DetailContent>
            </div>
            <Divider type="vertical" style={{ margin: 0 }} />
            <div>
              <DetailTitle strong>Domain:</DetailTitle>{' '}
              <DetailContent type="secondary">
                {form.getFieldValue(ExpFormField.DOMAIN)}
              </DetailContent>
            </div>
            {assignByFacet && (
              <div>
                <DetailTitle strong>Assign by:</DetailTitle>{' '}
                <DetailContent type="secondary">
                  {assignByFacet.title}
                </DetailContent>
              </div>
            )}
          </DetailWrapper>
          <DetailWrapper>
            {form.getFieldValue(ExpFormField.PRODUCT_MANAGER) && (
              <>
                <div>
                  <DetailTitle strong>Product manager:</DetailTitle>{' '}
                  <DetailContent type="secondary">
                    {form.getFieldValue(ExpFormField.PRODUCT_MANAGER)}
                  </DetailContent>
                </div>
                <Divider type="vertical" style={{ margin: 0 }} />
              </>
            )}
            {form.getFieldValue(ExpFormField.PRODUCT_ANALYST) && (
              <div>
                <DetailTitle strong>Product analyst:</DetailTitle>{' '}
                <DetailContent type="secondary">
                  {form.getFieldValue(ExpFormField.PRODUCT_ANALYST)}
                </DetailContent>
              </div>
            )}
          </DetailWrapper>
        </Overview>
      </Wrapper>
      {showEditExperimentModal && (
        <EditExperimentModal
          isCloneMode={isCloneMode}
          isEditMode={isEditMode}
          initializedCloneExperiment={initializedCloneExperiment}
          onCancel={() => {
            if (isUninitializedForm) {
              navigateTo(`/t/experiments`);
            } else {
              setInitializedCloneExperiment(true);
              setShowEditExperimentModal(false);
            }
          }}
          onOk={() => {
            setInitializedCloneExperiment(true);
            setShowEditExperimentModal(false);
          }}
        />
      )}
      {!!reviewResult && (
        <ExperimentReviewModal
          reviewResult={reviewResult}
          onCancel={() => {
            setReviewResult(null);
          }}
          submitForm={submitForm}
          onOk={() => {
            setReviewResult(null);
          }}
          isEditMode={isEditMode}
        />
      )}
    </>
  );
};

export const Wrapper = styled.div`
  align-items: center;
  background: white;
  border-bottom: 1px solid #0505050f;
  display: flex;
  gap: 8px;
  padding: 12px 32px;
  position: sticky;
  top: 0;
  z-index: 3;
`;

export const Overview = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  font-size: 14px;
  min-width: 200px;
`;

export const FirstRowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ExperimentName = styled.div`
  color: #161a1d;
  font-size: 14px;
  font-weight: 600;
`;

export const DetailWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const DescriptionWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DetailTitle = styled(Text)`
  color: #3d3d3d;
  font-size: 12px;
  white-space: nowrap;
`;

export const DetailContent = styled(Text)`
  color: #707070;
  font-size: 12px;
  white-space: nowrap;
`;

export const ActionWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-left: 16px;
`;

export default Header;
